import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { forensicsApi } from './api'
import type { ForensicsRequest, RunningForensicsEnvironment, ForensicsTool, ForensicsExaminableObject, ForensicsExaminableVersion, ForensicsVaultEnvironment } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'

type BackendResponse<T> = T | { success: boolean; data: T }

export const forensicsKeys = {
    all: ['forensics'] as const,
    environments: () => [...forensicsKeys.all, 'environments'] as const,
    environmentList: (params?: { vaultId?: number }) => [...forensicsKeys.environments(), params] as const,
    environment: (id: number) => [...forensicsKeys.all, 'environment', id] as const,
    versions: (envId: number) => [...forensicsKeys.all, 'versions', envId] as const,
    computeObjects: () => [...forensicsKeys.all, 'compute-objects'] as const,
    computeObjectVersions: (objectId: string) =>
        [...forensicsKeys.all, 'compute-objects', objectId, 'versions'] as const,
    requests: (objectId?: string | null) => [...forensicsKeys.all, 'requests', objectId ?? null] as const,
    request: (id: number) => [...forensicsKeys.all, 'request', id] as const,
    runningEnvironments: (vaultId?: string | number | null) => [...forensicsKeys.all, 'running-environments', vaultId ?? null] as const,
    tools: (vaultId?: string | number | null) => [...forensicsKeys.all, 'tools', vaultId ?? null] as const,
}

export function useForensicsEnvironments(params?: { vaultId?: number }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: forensicsKeys.environmentList(params),
        queryFn: async () => {
            const response = await forensicsApi.listEnvironments(params)
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useForensicsEnvironment(id: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

    return useQuery({
        queryKey: forensicsKeys.environment(id!),
        queryFn: async () => {
            const response = await forensicsApi.getEnvironment(id!)
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: !!id && isAuthenticated,
    })
}

export function useForensicsEnvironmentsByVault(vaultId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

    return useQuery({
        queryKey: forensicsKeys.environmentList({ vaultId: vaultId! }),
        queryFn: async () => {
            const response = await forensicsApi.listEnvironments({ vaultId: vaultId! })
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: !!vaultId && isAuthenticated,
    })
}

export function useCreateForensicsEnvironment() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Parameters<typeof forensicsApi.createEnvironment>[0]) =>
            forensicsApi.createEnvironment(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Environment Created', description: 'Forensics environment has been created successfully.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useUpdateForensicsEnvironment() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Parameters<typeof forensicsApi.updateEnvironment>[1] }) =>
            forensicsApi.updateEnvironment(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environment(variables.id) })
            toast({ title: 'Environment Updated', description: 'Forensics environment has been updated successfully.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useDeleteForensicsEnvironment() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => forensicsApi.deleteEnvironment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Environment Deleted', description: 'Forensics environment has been deleted.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useStartForensicsVersion() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ envId, data }: { envId: number; data: { objectId: string; objectVersionId: number } }) =>
            forensicsApi.startVersion(envId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environment(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.versions(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Version Started', description: 'Object version has been submitted for forensics analysis.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useStopForensicsVersion() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ envId, versionId }: { envId: number; versionId: number }) =>
            forensicsApi.stopVersion(envId, versionId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environment(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.versions(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Version Stopped', description: 'Forensics instance has been stopped.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

/**
 * The versions of one object that a forensics workstation can mount.
 *
 * Its own key, NOT the shared ['objects', id, 'versions'] one. That key is a contract used by the
 * object screens and returns every version; this returns only the mountable subset, and writing a
 * different shape into a shared key is how one caller poisons another's cache.
 */
export function useForensicsObjectVersions(objectId: string | null | undefined) {
    return useQuery({
        queryKey: forensicsKeys.computeObjectVersions(objectId || ''),
        enabled: Boolean(objectId),
        queryFn: async () => {
            if (!objectId) return []
            const response = await forensicsApi.listComputeObjectVersions(objectId)
            const backendData = response.data as BackendResponse<ForensicsExaminableVersion[]>
            const rows = (backendData as { data?: ForensicsExaminableVersion[] })?.data ?? backendData
            return Array.isArray(rows) ? rows : []
        },
    })
}

export function useComputeObjects() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

    return useQuery({
        queryKey: forensicsKeys.computeObjects(),
        queryFn: async () => {
            const response = await forensicsApi.listComputeObjects()
            const backendData = response.data as BackendResponse<ForensicsExaminableObject[]>
            const rows = (backendData as { data?: ForensicsExaminableObject[] })?.data ?? backendData
            return Array.isArray(rows) ? rows : []
        },
        enabled: isAuthenticated,
    })
}

// ── New request/session hooks (mirror Request Recovery: create + poll status) ────────

const unwrapForensics = <T,>(resp: any): T => (resp?.data?.data ?? resp?.data) as T
const FORENSICS_TERMINAL = ['Ready', 'Expired', 'Error']

/** Active (non-expired/-error) forensics sessions referencing an object. */
export function useForensicsRequests(objectId: string | null, enabled = true) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    return useQuery({
        queryKey: forensicsKeys.requests(objectId),
        queryFn: async () => unwrapForensics<ForensicsRequest[]>(await forensicsApi.listRequests(objectId ?? undefined)),
        enabled: !!objectId && isAuthenticated && enabled,
    })
}

/** A single request. When `poll` is true, refetches every 5s until a terminal status. */
export function useForensicsRequest(id: number | null, poll = false) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    return useQuery({
        queryKey: forensicsKeys.request(id!),
        queryFn: async () => unwrapForensics<ForensicsRequest>(await forensicsApi.getRequest(id!)),
        enabled: !!id && isAuthenticated,
        refetchInterval: (query) => {
            if (!poll) return false
            const status = (query.state.data as ForensicsRequest | undefined)?.status
            return status && FORENSICS_TERMINAL.includes(status) ? false : 5000
        },
    })
}

/** Live forensics environments in a vault. The Forensics dialog's first question. */
export function useVaultForensicsEnvironments(vaultId: string | number | null, enabled = true) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    return useQuery({
        queryKey: [...forensicsKeys.all, 'vault-environments', String(vaultId ?? '')],
        queryFn: async () =>
            unwrapForensics<ForensicsVaultEnvironment[]>(
                await forensicsApi.vaultEnvironments(vaultId!)),
        enabled: !!vaultId && isAuthenticated && enabled,
    })
}

/** Running multi-node environments a user can JOIN. */
export function useRunningForensicsEnvironments(vaultId: string | number | null, enabled = true) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    return useQuery({
        queryKey: forensicsKeys.runningEnvironments(vaultId),
        queryFn: async () => unwrapForensics<RunningForensicsEnvironment[]>(await forensicsApi.listRunningEnvironments(vaultId ?? undefined)),
        enabled: isAuthenticated && enabled,
    })
}

export function useCreateForensicsRequest() {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: (data: Parameters<typeof forensicsApi.createRequest>[0]) => forensicsApi.createRequest(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...forensicsKeys.all, 'requests'] })
            toast({ title: 'Forensics environment requested', description: 'The forensics environment is being provisioned.' })
        },
        onError: (error) => toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' }),
    })
}

export function useJoinForensicsEnvironment() {
    const { toast } = useToast()
    return useMutation({
        mutationFn: (data: Parameters<typeof forensicsApi.join>[0]) => forensicsApi.join(data),
        onSuccess: () => toast({ title: 'Join request sent', description: 'Your object is being added to the multi-node environment.' }),
        onError: (error) => toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' }),
    })
}

// ── Customer custom tools ────────────────────────────────────────────────────────────

export function useForensicsTools(vaultId: string | number | null, enabled = true) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    return useQuery({
        queryKey: forensicsKeys.tools(vaultId),
        queryFn: async () => unwrapForensics<ForensicsTool[]>(await forensicsApi.listTools(vaultId ?? undefined)),
        enabled: isAuthenticated && enabled,
    })
}

export function useAddForensicsTool() {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: (data: Parameters<typeof forensicsApi.addTool>[0]) => forensicsApi.addTool(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...forensicsKeys.all, 'tools'] })
            toast({ title: 'Tool added' })
        },
        onError: (error) => toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' }),
    })
}

export function useRemoveForensicsTool() {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: (id: number) => forensicsApi.removeTool(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...forensicsKeys.all, 'tools'] })
            toast({ title: 'Tool removed' })
        },
        onError: (error) => toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' }),
    })
}
